// Rule-based (deterministic) – gợi ý phong cách đầu tư theo Bát Tự (Ngũ Hành + Thập Thần)
// Lưu ý: Đây KHÔNG phải lời khuyên mua/bán. Chỉ là “góc nhìn” về khẩu vị rủi ro & kỷ luật.
// Output theo format project: score/notes/reasoning/recommendations (+ allocations/styleTags/disclaimer)

const clampScore = (score) => Math.max(1, Math.min(10, score));

const FIVE = ['Moc', 'Hoa', 'Tho', 'Kim', 'Thuy'];

const getNum = (obj, key, fallback = 0) => {
  const value = obj?.[key];
  return Number.isFinite(value) ? value : fallback;
};

const getTenGodCount = (counts, key) => getNum(counts, key, 0);

const getPct = (elementPercents, elementCounts, elementKey) => {
  // ưu tiên elementPercents (0..100), nếu không có thì tự tính theo counts (trên 8 chữ)
  const percent = elementPercents?.[elementKey];
  if (Number.isFinite(percent)) return percent;

  const total = FIVE.reduce((sum, k) => sum + getNum(elementCounts, k, 0), 0);
  if (!total) return 0;
  return (getNum(elementCounts, elementKey, 0) / total) * 100;
};

const countMissingElements = (elementCounts, elementPercents) => {
  // “Thiếu” định nghĩa cho MVP: phần trăm < 8% (tương đương ~0/8 hoặc rất ít)
  // Nếu counts có 0 → coi là thiếu chắc chắn.
  const missing = [];
  for (const key of FIVE) {
    const count = getNum(elementCounts, key, 0);
    const percent = getPct(elementPercents, elementCounts, key);
    if (count === 0 || percent < 8) missing.push(key);
  }
  return missing;
};

const mkReason = (factor, impact, explanation) => ({
  factor,
  impact,
  explanation
});

const uniq = (arr) => [...new Set(arr.filter(Boolean).map((item) => item.trim()))];

/**
 * investmentRule
 * @param {Object} input
 * @param {Object} input.tenGodCounts - ví dụ: { ChinhTai:2, ThienTai:1, KiepTai:0, ChinhQuan:1, ThienQuan:0, ThucThan:1, ThuongQuan:0, ChinhAn:1, ThienAn:0, TyKien:1 }
 * @param {Object} input.elementCounts - ví dụ: { Moc:2, Hoa:1, Tho:2, Kim:2, Thuy:1 }
 * @param {Object} input.elementPercents - optional: { Moc:25, Hoa:12.5, Tho:25, Kim:25, Thuy:12.5 }
 * @param {Object} input.relationsSummary - optional: { hop:1, xung:2, hinh:1, hai:0 }
 *
 * @returns {Object} { score, notes, recommendations, reasoning, allocations, styleTags, disclaimer, ruleMeta }
 *
 * score (1..10): "độ phù hợp với phong cách đầu tư kỷ luật/ổn định" (điểm cao → thiên về an toàn/kỷ luật).
 */
export function investmentRule({
  tenGodCounts = {},
  elementCounts = {},
  elementPercents = null,
  relationsSummary = null
} = {}) {
  const notes = [];
  const recommendations = [];
  const reasoning = [];
  let score = 6;

  // --- Ten Gods signals (động cơ & hành vi tài chính) ---
  const ChinhTai = getTenGodCount(tenGodCounts, 'ChinhTai');
  const ThienTai = getTenGodCount(tenGodCounts, 'ThienTai');
  const KiepTai = getTenGodCount(tenGodCounts, 'KiepTai');

  const ChinhQuan = getTenGodCount(tenGodCounts, 'ChinhQuan');
  const ThienQuan = getTenGodCount(tenGodCounts, 'ThienQuan');
  const ThucThan = getTenGodCount(tenGodCounts, 'ThucThan');
  const ThuongQuan = getTenGodCount(tenGodCounts, 'ThuongQuan');

  const ChinhAn = getTenGodCount(tenGodCounts, 'ChinhAn');
  const ThienAn = getTenGodCount(tenGodCounts, 'ThienAn');
  const AnTotal = ChinhAn + ThienAn;

  // --- Five elements (khẩu vị – thiên hướng) ---
  const pHoa = getPct(elementPercents, elementCounts, 'Hoa');
  const pTho = getPct(elementPercents, elementCounts, 'Tho');
  const pKim = getPct(elementPercents, elementCounts, 'Kim');
  const pThuy = getPct(elementPercents, elementCounts, 'Thuy');

  const missing = countMissingElements(elementCounts, elementPercents);

  // 1) Trục kỷ luật/ổn định: Chính Tài + Chính Quan (+ Kim)
  if (ChinhTai >= 2 && ChinhQuan >= 1) {
    score += 2;
    notes.push('Có xu hướng tích lũy ổn định và làm theo nguyên tắc (Chính Tài + Chính Quan).');
    recommendations.push('Ưu tiên chiến lược dài hạn, mua định kỳ (DCA), tuân thủ kỷ luật giải ngân.');
    reasoning.push(
      mkReason(
        'Chính Tài ≥ 2 + Chính Quan ≥ 1',
        2,
        'Khuynh hướng trọng tính ổn định, thích kế hoạch rõ ràng và quản trị rủi ro.'
      )
    );
  }

  if (pKim >= 22) {
    score += 1;
    notes.push('Kim tương đối vượng → có xu hướng kỷ luật tốt và chú trọng quản trị rủi ro.');
    recommendations.push('Dùng checklist trước khi mua: định giá, rủi ro, kế hoạch thoát lệnh, tỷ trọng.');
    reasoning.push(mkReason('Kim ≥ 22%', 1, 'Kim thường gắn với tính kỷ luật và nguyên tắc.'));
  }

  // 2) Trục biến động/momentum: Hỏa, Thương Quan, Thiên Quan, Thiên Tài
  const highVolSignals =
    pHoa >= 22 || ThuongQuan >= 2 || ThienQuan >= 2 || ThienTai >= 2 || KiepTai >= 1;

  if (pHoa >= 28) {
    score -= 2;
    notes.push('Hỏa vượng → dễ hưng phấn/ra quyết định nhanh, hợp nhịp thị trường biến động.');
    recommendations.push('Nên có giới hạn lỗ, giới hạn số lệnh/tuần; tránh FOMO theo tin nóng.');
    reasoning.push(mkReason('Hỏa ≥ 28%', -2, 'Hỏa cao thường làm tăng xung động giao dịch.'));
  } else if (pHoa >= 22) {
    score -= 1;
    notes.push('Hỏa khá mạnh → dễ thiên về “momentum”, cần kiểm soát cảm xúc.');
    recommendations.push('Viết quy tắc vào/ra lệnh rõ ràng; không tăng tỷ trọng khi đang cảm xúc mạnh.');
    reasoning.push(mkReason('22% ≤ Hỏa < 28%', -1, 'Hỏa khá mạnh → tăng nhịp ra quyết định.'));
  }

  if (ThienQuan >= 2) {
    score -= 1;
    notes.push('Thiên Quan mạnh → chịu áp lực tốt nhưng dễ chấp nhận rủi ro cao.');
    recommendations.push('Nên hạn chế đòn bẩy; đặt trần rủi ro mỗi giao dịch (vd 0.5–1% NAV).');
    reasoning.push(mkReason('Thiên Quan ≥ 2', -1, 'Thiên Quan thường kéo xu hướng liều hơn mức cần thiết.'));
  }

  if (ThuongQuan >= 2) {
    score -= 1;
    notes.push('Thương Quan mạnh → sáng tạo/đột phá nhưng dễ “cãi trend” hoặc phá kỷ luật.');
    recommendations.push('Nếu muốn lướt sóng, nên backtest/ghi nhật ký ít nhất 30–50 lệnh trước khi tăng vốn.');
    reasoning.push(mkReason('Thương Quan ≥ 2', -1, 'Dễ đổi phương pháp, quyết định theo cảm hứng.'));
  }

  if (ThienTai >= 2) {
    score -= 1;
    notes.push('Thiên Tài mạnh → hợp cơ hội/đa nguồn thu, nhưng cần kỷ luật chốt lời và quản trị vốn.');
    recommendations.push('Nên có kế hoạch chốt lời từng phần; tránh “all-in” theo một cơ hội.');
    reasoning.push(mkReason('Thiên Tài ≥ 2', -1, 'Thiên Tài thiên về cơ hội, dễ tăng rủi ro nếu thiếu kỷ luật.'));
  }

  // 3) Kiếp Tài: rủi ro hùn hạp/overbet
  if (KiepTai >= 2) {
    score -= 3;
    notes.push('Kiếp Tài vượng → dễ rủi ro khi hùn hạp, đòn bẩy, hoặc tăng cược khi thua.');
    recommendations.unshift(
      'Nên tránh hùn vốn/đầu tư theo nhóm thiếu minh bạch; ưu tiên giới hạn rủi ro theo ngày/tuần.'
    );
    reasoning.push(
      mkReason('Kiếp Tài ≥ 2', -3, 'Kiếp Tài thường làm tăng rủi ro hành vi (overtrade/overbet).')
    );
  } else if (KiepTai >= 1) {
    score -= 1;
    notes.push('Có Kiếp Tài → nên cảnh giác quyết định theo cảm xúc và các “deal” rủi ro.');
    recommendations.push('Luôn kiểm chứng thông tin; ưu tiên kênh minh bạch; hạn chế vay mượn để đầu tư.');
    reasoning.push(mkReason('Kiếp Tài = 1', -1, 'Tăng rủi ro hành vi nếu thiếu kỷ luật.'));
  }

  // 4) Ấn (Chính Ấn/Thiên Ấn): lợi thế phân tích nhưng dễ “học mãi không vào lệnh”
  if (AnTotal >= 3) {
    score -= 1;
    notes.push('Ấn mạnh → lợi thế nghiên cứu, nhưng dễ “phân tích quá mức” dẫn tới chậm hành động.');
    recommendations.push('Chuẩn hóa quy trình: đủ 3–5 tiêu chí là giải ngân nhỏ để kiểm chứng.');
    reasoning.push(mkReason('Chính Ấn + Thiên Ấn ≥ 3', -1, 'Dễ rơi vào phân tích kéo dài, bỏ lỡ cơ hội.'));
  } else if (AnTotal >= 2) {
    score += 1;
    notes.push('Ấn khá tốt → hợp đầu tư theo dữ liệu và học bài bản.');
    recommendations.push('Duy trì nhật ký giao dịch/đầu tư; định kỳ tổng kết sai lầm để cải thiện.');
    reasoning.push(mkReason('Chính Ấn + Thiên Ấn ≥ 2', 1, 'Giúp nâng chất lượng quyết định nhờ nghiên cứu.'));
  }

  // 5) Thực Thần: bền bỉ, “làm ra tiền nhờ năng lực”
  if (ThucThan >= 2) {
    score += 1;
    notes.push('Thực Thần tốt → thiên hướng bền bỉ, phù hợp tích lũy dài hạn và nâng kỹ năng kiếm tiền.');
    recommendations.push('Ưu tiên xây nền tài chính cá nhân, quỹ dự phòng; đầu tư song song với nâng kỹ năng.');
    reasoning.push(mkReason('Thực Thần ≥ 2', 1, 'Thực Thần hỗ trợ dòng tiền ổn định và tính bền bỉ.'));
  }

  // 6) Thiếu hành → thiếu cân bằng hành vi (rule mềm, không tuyệt đối hóa)
  if (missing.length >= 2) {
    score -= 1;
    notes.push(`Ngũ hành thiếu/nhược nhiều (${missing.join(', ')}) → dễ lệch phong cách, cần khung kỷ luật rõ.`);
    recommendations.push('Dùng quy tắc quản trị vốn cố định: tỷ trọng tối đa/mã, tối đa ngành, và ngưỡng cắt lỗ.');
    reasoning.push(
      mkReason(
        `Thiếu/nhược ≥ 2 hành (${missing.join(', ')})`,
        -1,
        'Mất cân bằng ngũ hành thường đi kèm lệch hành vi (quá nhanh/ quá chậm/ quá cứng/ quá cảm tính).'
      )
    );
  } else if (missing.length === 1) {
    notes.push(`Có 1 hành thiếu/nhược (${missing[0]}) → nên bù bằng thói quen quản trị phù hợp.`);
    recommendations.push('Thiết kế quy trình: (1) kế hoạch, (2) điểm mua, (3) điểm bán, (4) ghi nhật ký.');
    reasoning.push(
      mkReason(`Thiếu/nhược 1 hành (${missing[0]})`, 0, 'Rule nhẹ: dùng như gợi ý để bổ sung thói quen quản trị.')
    );
  }

  // 7) Thổ cao + Thủy thấp → rủi ro thanh khoản (BĐS/ tài sản nặng)
  if (pTho >= 28 && pThuy < 12) {
    score -= 1;
    notes.push('Thổ vượng + Thủy yếu → dễ thiên về tài sản “nặng” nhưng cần chú ý thanh khoản.');
    recommendations.push('Duy trì quỹ dự phòng tiền mặt; tránh dồn hết vào tài sản khó thoát khi thị trường xấu.');
    reasoning.push(mkReason('Thổ ≥ 28% và Thủy < 12%', -1, 'Thủy thấp → linh hoạt thanh khoản kém.'));
  }

  // 8) Kim quá cao + Thủy thấp → quá cứng nhắc, khó thích nghi khi đổi pha
  if (pKim >= 32 && pThuy < 12) {
    score -= 1;
    notes.push('Kim rất vượng nhưng Thủy yếu → kỷ luật tốt nhưng dễ cứng nhắc khi thị trường đổi pha.');
    recommendations.push('Đặt rule cập nhật định kỳ (hàng tháng/quý): nếu dữ liệu đổi, cho phép điều chỉnh có kiểm soát.');
    reasoning.push(mkReason('Kim ≥ 32% và Thủy < 12%', -1, 'Thiếu linh hoạt có thể khiến bỏ lỡ chu kỳ mới.'));
  }

  // 9) Hỏa cao nhưng có Kim trung bình → có thể “momentum có kỷ luật”
  if (pHoa >= 22 && pKim >= 18) {
    score += 1;
    notes.push('Hỏa khá mạnh nhưng có Kim hỗ trợ → có thể theo xu hướng nhưng vẫn giữ kỷ luật.');
    recommendations.push('Nếu giao dịch ngắn hạn: luôn có điểm vô hiệu (invalid) và tỷ lệ R:R tối thiểu.');
    reasoning.push(mkReason('Hỏa ≥ 22% và Kim ≥ 18%', 1, 'Kim giúp “neo” kỷ luật cho hành vi nhanh của Hỏa.'));
  }

  // --- Optional relations summary (xung/hinh/hai) → gợi ý buffer rủi ro ---
  const xung = getNum(relationsSummary, 'xung');
  const hinh = getNum(relationsSummary, 'hinh');
  const hai = getNum(relationsSummary, 'hai');
  if (xung + hinh + hai >= 4) {
    score -= 1;
    notes.push('Quan hệ xung/hình/hại nhiều → ưu tiên biên an toàn và dự phòng rủi ro cao hơn.');
    recommendations.push('Giữ buffer tiền mặt; giảm số lệnh; tránh dùng margin/đòn bẩy trong giai đoạn biến động.');
    reasoning.push(
      mkReason(
        'Xung + Hình + Hại ≥ 4 (optional)',
        -1,
        'Rule mềm: dùng như tín hiệu tăng dự phòng, không phải dự đoán chắc chắn.'
      )
    );
  }

  // --- Profile inference (để gợi ý phân bổ) ---
  // score cao → thiên kỷ luật/an toàn; score thấp → thiên biến động/đầu cơ (cần quản trị chặt)
  const styleTags = [];

  const conservativeFlag =
    score >= 8 || (ChinhTai >= 2 && (ChinhQuan >= 1 || pKim >= 22) && !highVolSignals);

  const aggressiveFlag =
    score <= 4 || (highVolSignals && (KiepTai >= 1 || pHoa >= 22 || ThienTai >= 2));

  let profile = 'balanced';
  if (conservativeFlag) profile = 'conservative';
  else if (aggressiveFlag) profile = 'aggressive';

  if (profile === 'conservative') {
    styleTags.push('Ổn định', 'Kỷ luật', 'Dài hạn');
  } else if (profile === 'aggressive') {
    styleTags.push('Biến động', 'Cơ hội', 'Cần quản trị rủi ro');
  } else {
    styleTags.push('Cân bằng', 'Kế hoạch', 'Linh hoạt có kiểm soát');
  }

  // Gợi ý phân bổ (không phải lời khuyên đầu tư)
  // Lưu ý: tỷ trọng chỉ là khuyến nghị hành vi, người dùng tự điều chỉnh theo mục tiêu/tuổi/thu nhập.
  const allocations = (() => {
    if (profile === 'conservative') {
      return {
        profile,
        cashPctRange: '30–45%',
        stockPctRange: '40–55%',
        realEstatePctRange: '0–20%',
        note: 'Ưu tiên quỹ dự phòng và chiến lược tích lũy; hạn chế đòn bẩy và giao dịch dày.'
      };
    }
    if (profile === 'aggressive') {
      return {
        profile,
        cashPctRange: '10–20%',
        stockPctRange: '65–85%',
        realEstatePctRange: '0–20%',
        note: 'Nếu chọn phong cách biến động, nên có giới hạn lỗ và giới hạn tỷ trọng mỗi mã/ngành.'
      };
    }
    return {
      profile,
      cashPctRange: '15–30%',
      stockPctRange: '50–65%',
      realEstatePctRange: '10–30%',
      note: 'Giữ cân bằng giữa tích lũy và linh hoạt; ưu tiên kế hoạch giải ngân theo mốc thời gian.'
    };
  })();

  recommendations.unshift(
    'Không dùng tiền vay/đòn bẩy nếu chưa có hệ thống quản trị rủi ro và lịch sử giao dịch ổn định.',
    'Thiết lập 3 thứ trước khi mua: (1) lý do mua, (2) điều kiện bán, (3) tỷ trọng tối đa.'
  );

  return {
    score: clampScore(score),
    notes: uniq(notes),
    recommendations: uniq(recommendations),
    reasoning,
    allocations,
    styleTags,
    disclaimer: 'Kết quả chỉ mang tính tham khảo/học thuật/giải trí',
    ruleMeta: {
      ruleId: 'INVESTMENT_STYLE_PROFILE',
      ruleVersion: 'rules-v1',
      scoreMeaning:
        'Điểm càng cao → thiên về kỷ luật/ổn định; điểm thấp → thiên biến động/cơ hội (cần quản trị rủi ro chặt).',
      inputs: ['tenGodCounts', 'elementCounts/elementPercents', 'relationsSummary (optional)']
    }
  };
}

export default investmentRule;
